/**
 * SectionCard — dashfolio-DNA section card
 *
 * 용도: Dashboard · Lab 등에서 사용하는 기본 섹션 카드.
 *       dashfolio 핵심 패턴 = 불투명 surface + 1px hairline + 12px radius.
 *
 * 철학 (docs/design/dashfolio-dna.md §2.5 · §3.1):
 *   - Vibrancy blur 를 쓰지 않는다 (다크에서 혼탁 + 성능 부담).
 *   - Drop shadow 최소 — hairline 으로 경계 표현.
 *   - 헤딩 / rows 구조는 호출자가 직접 구성.
 *
 * 접근성:
 *   - <section> 기본. `aria-labelledby` 등은 호출자 props 로 투명 전달.
 *   - 호버/포커스 시 border 가 `hairline-strong` 으로 강조되길 원하면
 *     `hoverable` 플래그 사용 (정적 섹션에서는 생략).
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../utils/cn';

type Padding = 'none' | 'sm' | 'md' | 'lg';

interface SectionCardProps extends HTMLAttributes<HTMLElement> {
  /** 내부 padding. 기본 `md` = 20px (p-5). */
  padding?: Padding;
  /** 호버 시 border 강조 여부. 기본 false (정적 섹션). */
  hoverable?: boolean;
  /** 고정 shadow. 기본 없음. `subtle` = 1px hairline + 약한 drop. */
  shadow?: 'none' | 'subtle';
  children: ReactNode;
}

const PADDING_CLASS: Record<Padding, string> = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
};

/**
 * dashfolio 기본 카드 — surface + hairline + 12px radius.
 */
export function SectionCard({
  padding = 'md',
  hoverable = false,
  shadow = 'none',
  className,
  children,
  ...rest
}: SectionCardProps) {
  return (
    <section
      className={cn(
        'rounded-[var(--radius-admin-lg)] border border-[var(--color-admin-hairline)] bg-[var(--color-admin-surface)]',
        PADDING_CLASS[padding],
        shadow === 'subtle' && 'shadow-[var(--shadow-admin-card)]',
        hoverable &&
          'transition-[border-color,background-color] duration-[var(--duration-admin-base)] ease-[var(--ease-admin-out)] hover:border-[var(--color-admin-hairline-strong)] hover:bg-[var(--color-admin-surface-2)]',
        className,
      )}
      {...rest}
    >
      {children}
    </section>
  );
}

SectionCard.displayName = 'SectionCard';
